import React, { Fragment, FC } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';

import { PATHS } from 'src/common/constants';
import getClassNames from 'src/common/utils/getClassNames';
import appendTrailingSlash from 'src/common/utils/appendTrailingSlash';
import formatLocationName from 'src/search/utils/formatLocationName';
import { Breadcrumb } from 'src/common/types/api/v4/boats/search';
import Icon from 'src/common/components/Icon';
import BreadcrumbElement from 'src/common/components/BreadcrumbElement';
import styles from './Breadcrumbs.module.scss';

export type BreadcrumbsProps = {
  breadcrumbs: Breadcrumb[] | null;
  category?: Breadcrumb;
  classNameModifier?: 'listingPage';
  renderLinks?: boolean;
  commaSeparated?: boolean;
  previewMode?: boolean;
};

const Breadcrumbs: FC<BreadcrumbsProps> = ({
  breadcrumbs,
  category,
  classNameModifier,
  renderLinks = true,
  commaSeparated = false,
  previewMode,
}) => {
  const [t] = useTranslation('admin-listing-preview');
  const { asPath } = useRouter();
  const showPlaceholder = previewMode && !breadcrumbs?.length;
  // Get pathname without query string
  const pathname = asPath.split('?')[0];

  if (!previewMode && !breadcrumbs) {
    return null;
  }

  const icon = commaSeparated ? (
    <span className={getClassNames(styles, 'icon', classNames(classNameModifier, { commaSeparated }))}>,</span>
  ) : (
    <Icon
      id="common/right-arrow"
      className={getClassNames(styles, 'icon', classNameModifier)}
    />
  );

  return (
    <div className={getClassNames(styles, 'root', classNameModifier)}>
      {breadcrumbs?.map(({ indexed = false, name, slug }, index) => {
        const breadcrumbName = formatLocationName(name);
        const isLastBreadcrumb = index === breadcrumbs.length - 1;
        const showIcon = !isLastBreadcrumb || category;
        const path = appendTrailingSlash(`${PATHS.SEARCH}${slug === 'boat-rental' ? '' : slug}`);
        const renderLink = (
          // Whether we want links on any breadcrumb or not (ListingCard doesn't have links)
          renderLinks
          // We don't want to use a link if the path is self referential.
          && path !== pathname
          // Add link to breadcrumb element for indexed pages only
          && indexed);

        return (
          <Fragment key={slug}>
            <span className={styles.wrapper}>
              <BreadcrumbElement
                path={path}
                renderLink={renderLink}
              >
                {breadcrumbName}
              </BreadcrumbElement>
              {showIcon && icon}
            </span>
          </Fragment>
        );
      })}
      {!previewMode && category && category.slug && (
        <BreadcrumbElement
          path={appendTrailingSlash(`${PATHS.SEARCH}${category.slug}`)}
          renderLink={!!category.indexed && renderLinks}
        >
          {category.name}
        </BreadcrumbElement>
      )}
      {showPlaceholder && (
        <div>{t('breadcrumbs--placeholder')}</div>
      )}
    </div>
  );
};

export default Breadcrumbs;
